import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send, RotateCcw, Loader2, FileText, AlertCircle, Check, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useScriptEditor } from '@/hooks/useScriptEditor';
import { useFavorites } from '@/hooks/useFavorites';
import { countWords, validateContent } from '@/utils/sanitizeContent';
// DashboardSidebar and DashboardHeader are now in DashboardLayout
import { useIsMobile } from '@/hooks/use-mobile';
import { EditorInsightsPanel } from '@/components/EditorInsightsPanel';

const MAX_CONTENT_LENGTH = 10000;
const WORDS_PER_MINUTE = 150;

const Edytor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  
  const scriptId = searchParams.get('id');
  const tableParam = searchParams.get('table') as 'content_ideas' | 'user_generated_content' | null;
  
  const {
    scriptData,
    editedContent,
    isLoading,
    isSaving,
    lastSaved,
    hasChanges,
    setEditedContent,
    saveChanges,
    restoreOriginal,
    loadScript,
  } = useScriptEditor();

  const { toggleFavorite, isLoading: isFavoriteLoading } = useFavorites();
  const [isFavorited, setIsFavorited] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showSaveIndicator, setShowSaveIndicator] = useState(false);

  // Load script on mount
  useEffect(() => {
    if (scriptId && tableParam) {
      loadScript(scriptId, tableParam);
    }
  }, [scriptId, tableParam, loadScript]);

  // Sync favorite status from scriptData
  useEffect(() => {
    if (scriptData) {
      setIsFavorited(scriptData.isFavorited);
    }
  }, [scriptData]);

  // Validate content as user types
  useEffect(() => {
    const validation = validateContent(editedContent);
    setValidationError(validation.isValid ? null : validation.error || null);
  }, [editedContent]);

  // Show save indicator when lastSaved changes
  useEffect(() => {
    if (lastSaved) {
      setShowSaveIndicator(true);
      const timer = setTimeout(() => setShowSaveIndicator(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [lastSaved]);

  const wordCount = countWords(editedContent);
  const charCount = editedContent.length;
  const readingTimeMinutes = Math.max(1, Math.ceil(wordCount / WORDS_PER_MINUTE));
  const hasEditedVersion = scriptData?.editedContent !== null && scriptData?.editedContent !== undefined;

  const handleSave = async () => {
    const saved = await saveChanges();
    return saved;
  };

  const handleSendToPrompter = async () => {
    if (hasChanges) {
      const saved = await saveChanges();
      if (!saved) return;
    }
    navigate(`/prompter?id=${scriptId}&table=${tableParam}`);
  };

  const handleRestoreOriginal = async () => {
    await restoreOriginal();
    setShowRestoreDialog(false);
  };

  const handleToggleFavorite = async () => {
    if (!scriptId || !tableParam) return;
    const source = tableParam === 'user_generated_content' ? 'user_generated' : 'scraped';
    const success = await toggleFavorite(scriptId, isFavorited, source);
    if (success) {
      setIsFavorited(!isFavorited);
    }
  };

  // Empty state - no script selected
  if (!scriptId || !tableParam) {
    return (
      <main className="flex-1 p-3 md:p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
            <FileText className="w-10 h-10 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-semibold mb-3">Nie wybrano scenariusza</h1>
          <p className="text-muted-foreground mb-6">Wybierz scenariusz z biblioteki, aby rozpocząć edycję.</p>
          <Button onClick={() => navigate('/biblioteka')}><ArrowLeft className="w-4 h-4 mr-2" />Wróć do biblioteki</Button>
        </div>
      </main>
    );
  }

  if (!isLoading && !scriptData) {
    return (
      <main className="flex-1 p-3 md:p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-destructive" />
          </div>
          <h1 className="text-2xl font-semibold mb-3">Nie znaleziono scenariusza</h1>
          <p className="text-muted-foreground mb-6">Scenariusz mógł zostać usunięty lub nie masz do niego dostępu.</p>
          <Button onClick={() => navigate('/biblioteka')}><ArrowLeft className="w-4 h-4 mr-2" />Wróć do biblioteki</Button>
        </div>
      </main>
    );
  }

  return (
    <>
      {/* Header */}
        <header className="border-b bg-card/50 backdrop-blur-sm px-4 md:px-6 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/biblioteka')}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Biblioteka
            </Button>

            <h1 className="text-base font-medium hidden md:block">Edytor scenariusza</h1>

            {/* Save indicator */}
            <div className="w-24 flex justify-end">
              <div
                className={`flex items-center gap-1.5 text-sm text-green-600 transition-opacity duration-300 ${
                  showSaveIndicator ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <Check className="w-4 h-4" />
                Zapisano
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            {isLoading ? (
              <div className="space-y-6">
                <Skeleton className="h-10 w-3/4" />
                <Skeleton className="h-5 w-1/2" />
                <Skeleton className="h-[500px] w-full" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Title Section */}
                <div className="space-y-2">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground leading-tight">
                    {scriptData?.title || 'Bez tytułu'}
                  </h2>
                  
                  {/* Metadata row */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" />
                      {wordCount} słów
                    </span>
                    <span className="text-muted-foreground/50">|</span>
                    <span>~{readingTimeMinutes} min czytania</span>
                  </div>
                </div>

                {/* Insights Panel */}
                {scriptData?.insights && (
                  <EditorInsightsPanel
                    perplexityInsights={scriptData.insights.perplexityInsights}
                    altHooks={scriptData.insights.altHooks}
                    textOverlays={scriptData.insights.textOverlays}
                    improvementsApplied={scriptData.insights.improvementsApplied}
                  />
                )}

                {/* Restore original button */}
                {hasEditedVersion && (
                  <button
                    onClick={() => setShowRestoreDialog(true)}
                    disabled={isSaving}
                    className="flex items-center gap-1.5 text-sm text-amber-600 hover:text-amber-700 transition-colors disabled:opacity-50"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Przywróć oryginał
                  </button>
                )}

                {/* Editor Area */}
                <div className="relative">
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full min-h-[500px] rounded-xl border border-border bg-background p-6 resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-150 font-sans text-base leading-relaxed"
                    placeholder="Zacznij pisać lub wklej swój tekst..."
                    style={{ lineHeight: 1.6 }}
                  />
                </div>

                {/* Stats below editor */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{wordCount} słów</span>
                    <span className="text-muted-foreground/50">|</span>
                    <span className={charCount > MAX_CONTENT_LENGTH ? 'text-destructive' : ''}>
                      {charCount.toLocaleString()} / {MAX_CONTENT_LENGTH.toLocaleString()} znaków
                    </span>
                    <span className="text-muted-foreground/50">|</span>
                    <span>~{readingTimeMinutes} min czytania</span>
                  </div>
                  
                  {validationError && (
                    <span className="text-sm text-destructive">{validationError}</span>
                  )}
                </div>

                {/* Action Buttons */}
                <div className={`flex gap-3 ${isMobile ? 'flex-col' : 'justify-end'}`}>
                  <Button
                    variant="outline"
                    onClick={handleSendToPrompter}
                    disabled={isLoading || !!validationError}
                    className={isMobile ? 'w-full' : ''}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Wyślij do promptera
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={handleToggleFavorite}
                    disabled={isFavoriteLoading}
                    className={isMobile ? 'w-full' : ''}
                  >
                    <Heart 
                      className={`w-4 h-4 mr-2 transition-colors ${isFavorited ? 'fill-red-500 text-red-500' : ''}`} 
                    />
                    {isFavorited ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
                  </Button>
                  
                  <Button
                    onClick={handleSave}
                    disabled={!hasChanges || isSaving || !!validationError}
                    className={isMobile ? 'w-full' : ''}
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : null}
                    Zapisz zmiany
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Przywrócić oryginalny tekst?</AlertDialogTitle>
            <AlertDialogDescription>
              Stracisz wszystkie wprowadzone zmiany. Tej operacji nie można cofnąć.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestoreOriginal}>
              Przywróć
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Edytor;
